import type { Key } from "react-aria-components";

import {
	LayoutRight,
	Map02,
	MarkerPin01,
	Modem02,
	Save01,
} from "@untitledui/icons";
import { useMemo } from "react";

import { Button } from "@/components/ui/buttons/button";
import {
	type ContextMenuAnchorPoint,
	SimplePointerContextMenu,
} from "@/components/ui/context-menu/pointer-context-menu";

import type { LocationManagementState } from "../../hooks/useLocationManagementState";
import type { ZoneBoundaryDrawConfig } from "../../map/draw/useMapboxDraw";
import type { MapDrillBreadcrumb } from "../../utils/location/location-map-utils";

import {
	buildFloorBoundaryOverlays,
	floorIdFromBoundaryFeature,
	resolveFloorOutlineMode,
} from "../../map/floors/floor-boundary-overlays";
import { floorMapImages } from "../../map/floors/floor-map-images";
import { Map as MapView } from "../../map/Map";
import { MapDrillBreadcrumbNav } from "./map-pane/map-drill-breadcrumb";
import { MapEmptyState } from "./map-pane/map-empty-state";
import { UnmappedLocationsBanner } from "./map-pane/unmapped-locations-banner";

type LocationMapViewPaneProps = {
	canPlaceSensorByMapClick: boolean;
	contextMenuItems: Array<{ id: string; label: string }>;
	editingSensorHasCoordinates: boolean;
	featureContextMenu: MapFeatureContextMenuState | null;
	floorBoundaryDraw?: ZoneBoundaryDrawConfig;
	handleBackToAllLocations: () => void;
	handleBreadcrumbClick: (crumb: MapDrillBreadcrumb) => void;
	handleFeatureContextMenuAction: (key: Key) => void;
	handleMarkerContextMenu: (
		feature: GeoJSON.Feature,
		point: ContextMenuAnchorPoint,
	) => void;
	handleOverlayContextMenu: (
		feature: GeoJSON.Feature,
		point: ContextMenuAnchorPoint,
	) => void;
	isOverlayDrawer: boolean;
	isSidebarOpen: boolean;
	locationInstallationCount: number;
	mapCameraPadding: {
		bottom: number;
		left: number;
		right: number;
		top: number;
	};
	mapCenter: [number, number];
	mapState: LocationManagementState;
	onCloseSidebar: () => void;
	onOpenCreateInstallation: () => void;
	onOpenCreateSensor: (zoneId?: null | string) => void;
	onOpenCreateZone: () => void;
	onOpenSidebar: () => void;
	setFeatureContextMenu: (next: MapFeatureContextMenuState | null) => void;
	setTrackedMapViewportCenter: (center: [number, number]) => void;
	showAllUnmappedEmpty: boolean;
	showUnmappedBanner: boolean;
	sidebarToggleLabel: string;
};

type MapFeatureContextMenuState = {
	anchor: ContextMenuAnchorPoint;
	feature: GeoJSON.Feature;
	kind: "floor" | "sensor" | "zone";
};

export const LocationMapViewPane = ({
	canPlaceSensorByMapClick,
	contextMenuItems,
	editingSensorHasCoordinates,
	featureContextMenu,
	floorBoundaryDraw,
	handleBackToAllLocations,
	handleBreadcrumbClick,
	handleFeatureContextMenuAction,
	handleMarkerContextMenu,
	handleOverlayContextMenu,
	isOverlayDrawer,
	isSidebarOpen,
	locationInstallationCount,
	mapCameraPadding,
	mapCenter,
	mapState,
	onCloseSidebar,
	onOpenCreateInstallation,
	onOpenCreateSensor,
	onOpenCreateZone,
	onOpenSidebar,
	setFeatureContextMenu,
	setTrackedMapViewportCenter,
	showAllUnmappedEmpty,
	showUnmappedBanner,
	sidebarToggleLabel,
}: LocationMapViewPaneProps) => {
	const {
		activeFeature,
		canEditSensorDetails,
		canEditZoneDetails,
		displayMapMarkers,
		editingSensorId,
		editingZoneId,
		emphasizedLocationClusterIds,
		emphasizedSensorIds,
		fitMapMarkers,
		handleMapCoordinateChange,
		handleMarkerClick,
		handleMarkerDragEnd,
		handleMarkerEdit,
		handleMarkerHover,
		handleMarkerHoverEnd,
		handleOverlayClick,
		handleOverlayDoubleClick,
		handlePopupClose,
		isEditing,
		isFloorEditActive,
		isLocationSelected,
		location,
		mapBreadcrumbs,
		mapFocusFitKey,
		mapMarkers,
		showEmptyFocusedZoneMessage,
		unmappedLocationCount,
		visibleFloorId,
		visibleMapOverlays,
		zoneBoundaryDraw,
		zones,
	} = mapState;

	const floorImages = useMemo(
		() =>
			isLocationSelected && visibleFloorId
				? floorMapImages(
						(location.floors ?? []).filter(
							(floor) =>
								floor.id === visibleFloorId &&
								resolveFloorOutlineMode(floor) === "image",
						),
					)
				: [],
		[isLocationSelected, location.floors, visibleFloorId],
	);
	const floorOutlines = useMemo(
		() =>
			isLocationSelected && visibleFloorId
				? buildFloorBoundaryOverlays(
						(location.floors ?? []).filter(
							(floor) => floor.id === visibleFloorId,
						),
						location.zones ?? [],
					)
				: [],
		[isLocationSelected, location.floors, location.zones, visibleFloorId],
	);
	const drawingFloorId = floorIdFromBoundaryFeature(
		floorBoundaryDraw?.featureId,
	);
	const mapOverlays = useMemo(() => {
		if (!visibleMapOverlays) return undefined;
		if (!drawingFloorId) return visibleMapOverlays;
		const next = visibleMapOverlays.filter(
			(feature) => feature.properties?.floorId !== drawingFloorId,
		);
		return next.length > 0 ? next : undefined;
	}, [drawingFloorId, visibleMapOverlays]);
	const activeBoundaryDraw = zoneBoundaryDraw ?? floorBoundaryDraw;
	const hasFloorOnMap =
		floorImages.length > 0 ||
		floorOutlines.length > 0 ||
		Boolean(floorBoundaryDraw);

	return (
		<>
			{isLocationSelected && locationInstallationCount === 0 && !isEditing ? (
				<div className="relative flex min-h-0 flex-1 flex-col">
					<MapDrillBreadcrumbNav
						crumbs={mapBreadcrumbs}
						onCrumbClick={handleBreadcrumbClick}
					/>
					<MapEmptyState
						action={
							<div className="flex flex-wrap items-center justify-center gap-2">
								<Button
									color="primary"
									onClick={onOpenCreateInstallation}
									size="sm"
								>
									Add Installation
								</Button>
								<Button
									color="secondary"
									onClick={handleBackToAllLocations}
									size="sm"
								>
									Back to All Locations
								</Button>
							</div>
						}
						className="min-h-0 flex-1"
						description="Add an installation for this location before creating zones."
						icon={<Save01 />}
						title="No Installation Found"
					/>
				</div>
			) : isLocationSelected &&
			  zones.length === 0 &&
			  !isEditing &&
			  !hasFloorOnMap ? (
				<div className="relative flex min-h-0 flex-1 flex-col">
					<MapDrillBreadcrumbNav
						crumbs={mapBreadcrumbs}
						onCrumbClick={handleBreadcrumbClick}
					/>
					<MapEmptyState
						action={
							<div className="flex flex-wrap items-center justify-center gap-2">
								<Button color="primary" onClick={onOpenCreateZone} size="sm">
									Add Zone
								</Button>
								<Button
									color="secondary"
									onClick={handleBackToAllLocations}
									size="sm"
								>
									Back to All Locations
								</Button>
							</div>
						}
						className="min-h-0 flex-1"
						description="Add a zone for this location to start placing sensors on the map."
						icon={<Map02 />}
						title="No Zones Found"
					/>
				</div>
			) : isLocationSelected &&
			  mapMarkers.length === 0 &&
			  !isEditing &&
			  !hasFloorOnMap ? (
				<div className="relative flex min-h-0 flex-1 flex-col">
					<MapDrillBreadcrumbNav
						crumbs={mapBreadcrumbs}
						onCrumbClick={handleBreadcrumbClick}
					/>
					<MapEmptyState
						action={
							<div className="flex flex-wrap items-center justify-center gap-2">
								<Button
									color="primary"
									onClick={() => onOpenCreateSensor(null)}
									size="sm"
								>
									Add Sensor
								</Button>
								<Button
									color="secondary"
									onClick={handleBackToAllLocations}
									size="sm"
								>
									Back to All Locations
								</Button>
							</div>
						}
						className="min-h-0 flex-1"
						description="Sensors in this location do not have map coordinates yet."
						icon={<Modem02 />}
						title="No Mapped Sensors"
					/>
				</div>
			) : isLocationSelected &&
			  showEmptyFocusedZoneMessage &&
			  !isEditing &&
			  !floorBoundaryDraw ? (
				<div className="relative flex min-h-0 flex-1 flex-col">
					<MapDrillBreadcrumbNav
						crumbs={mapBreadcrumbs}
						onCrumbClick={handleBreadcrumbClick}
					/>
					<MapEmptyState
						action={
							<div className="flex flex-wrap items-center justify-center gap-2">
								<Button
									color="primary"
									onClick={() => {
										const zoneCrumb = [...mapBreadcrumbs]
											.reverse()
											.find((crumb) => crumb.zoneId);
										onOpenCreateSensor(zoneCrumb?.zoneId ?? null);
									}}
									size="sm"
								>
									Add Sensor
								</Button>
								<Button
									color="secondary"
									onClick={handleBackToAllLocations}
									size="sm"
								>
									Back to All Locations
								</Button>
							</div>
						}
						className="min-h-0 flex-1"
						description="The selected zone does not have any mapped sensors yet."
						icon={<Modem02 />}
						title="No Mapped Sensors in Zone"
					/>
				</div>
			) : showAllUnmappedEmpty ? (
				<div className="relative flex min-h-0 flex-1 flex-col">
					<MapEmptyState
						action={
							<Button color="secondary" onClick={onOpenSidebar} size="sm">
								Open locations
							</Button>
						}
						className="min-h-0 flex-1"
						description="This organization has locations, but none have coordinates or mapped sensors yet. Pick a location in the sidebar to configure it."
						icon={<MarkerPin01 />}
						title="Locations need a map position"
					/>
				</div>
			) : (
				<div className="relative min-h-0 flex-1">
					{showUnmappedBanner ? (
						<UnmappedLocationsBanner count={unmappedLocationCount} />
					) : null}
					{activeBoundaryDraw ? null : (
						<MapDrillBreadcrumbNav
							crumbs={mapBreadcrumbs}
							onCrumbClick={handleBreadcrumbClick}
						/>
					)}
					<MapView
						activeFeature={activeFeature ?? null}
						animateInitialFit
						cameraPadding={mapCameraPadding}
						center={mapCenter}
						className="h-full min-h-0 w-full"
						compact
						emphasizedLocationClusterIds={emphasizedLocationClusterIds}
						emphasizedSensorIds={emphasizedSensorIds}
						fitBoundsKey={`${mapFocusFitKey}|sb:${isSidebarOpen ? "1" : "0"}`}
						fitMarkers={fitMapMarkers}
						floorImages={floorImages}
						markers={displayMapMarkers}
						onMapClick={
							canPlaceSensorByMapClick ? handleMapCoordinateChange : undefined
						}
						onMarkerClick={handleMarkerClick}
						onMarkerContextMenu={
							isLocationSelected && canEditSensorDetails
								? handleMarkerContextMenu
								: undefined
						}
						onMarkerDoubleClick={
							isLocationSelected && canEditSensorDetails
								? handleMarkerEdit
								: undefined
						}
						onMarkerDragEnd={
							isLocationSelected &&
							canEditSensorDetails &&
							editingSensorId &&
							editingSensorHasCoordinates
								? handleMarkerDragEnd
								: undefined
						}
						onMarkerEdit={
							isLocationSelected && canEditSensorDetails
								? handleMarkerEdit
								: undefined
						}
						onMarkerHoverEnd={
							isLocationSelected ? handleMarkerHoverEnd : undefined
						}
						onMarkerHoverStart={
							isLocationSelected ? handleMarkerHover : undefined
						}
						onMoveEnd={setTrackedMapViewportCenter}
						onOverlayClick={isLocationSelected ? handleOverlayClick : undefined}
						onOverlayContextMenu={
							isLocationSelected && canEditZoneDetails
								? handleOverlayContextMenu
								: undefined
						}
						onOverlayDoubleClick={
							isLocationSelected && canEditZoneDetails
								? handleOverlayDoubleClick
								: undefined
						}
						onPopupClose={handlePopupClose}
						overlayPassthroughZoneClicks={isFloorEditActive}
						overlayPassthroughZoneId={editingZoneId ?? undefined}
						overlays={isLocationSelected ? mapOverlays : undefined}
						overlaysInteractive={isLocationSelected}
						refitOnMarkerChange={false}
						suppressPopup={!isLocationSelected || !!editingSensorId}
						zoneBoundaryDraw={
							isLocationSelected ? activeBoundaryDraw : undefined
						}
					/>
					<SimplePointerContextMenu
						anchor={featureContextMenu?.anchor ?? null}
						isOpen={featureContextMenu !== null}
						items={contextMenuItems}
						onAction={handleFeatureContextMenuAction}
						onOpenChange={(isOpen) => {
							if (!isOpen) setFeatureContextMenu(null);
						}}
					/>
				</div>
			)}

			{!isSidebarOpen ? (
				<Button
					aria-label={sidebarToggleLabel}
					className="absolute top-3 right-3 z-20 shadow-sm md:top-3 md:right-3"
					color="secondary"
					iconLeading={LayoutRight}
					onClick={onOpenSidebar}
					size="sm"
				/>
			) : null}

			{/* Dim mask on mobile only — desktop overlaps the map with no mask. */}
			{isOverlayDrawer ? (
				<button
					aria-label="Close sidebar"
					className="absolute inset-0 z-30 rounded-lg bg-neutral-900/40 xl:hidden"
					onClick={onCloseSidebar}
					type="button"
				/>
			) : null}
		</>
	);
};

export type { MapFeatureContextMenuState };
